import { useUpdateSettlement } from '@services/settlements/mutations/useUpdateSettlement'
import type {
  ProrationBase,
  SettlementDetail,
  SettlementUpdate
} from '@services/settlements/types'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { typedNumber } from '../format'

export type SettlementFormValues = {
  title: string
  period_start: string
  period_end: string
  costs_period_start: string
  costs_period_end: string
  proration_base: ProrationBase
  period_months_override: string
  hot_water_price_per_m3: string
  hot_water_shared_annual: string
  cold_water_price_per_m3: string
  cold_water_shared_annual: string
  heat_water_price_per_m3: string
  heat_water_base_annual: string
  heating_annual: string
  use_coefficients: boolean
  heating_coefficients: string[]
  electricity_annual_consumption_cost: string
  electricity_annual_fixed_cost: string
  electricity_annual_kwh: string
  electricity_bill_period_start: string
  electricity_bill_period_end: string
  tenant_note: string
  internal_note: string
}

const text = (value: string | null | undefined) => value ?? ''
const orNull = (value: string) => (value === '' ? null : value)

function toFormValues(settlement: SettlementDetail): SettlementFormValues {
  return {
    title: text(settlement.title),
    period_start: settlement.period_start,
    period_end: settlement.period_end,
    costs_period_start: text(settlement.costs_period_start),
    costs_period_end: text(settlement.costs_period_end),
    proration_base: settlement.proration_base ?? 'HALF_MONTHS',
    period_months_override: text(settlement.period_months_override),
    hot_water_price_per_m3: text(settlement.hot_water_price_per_m3),
    hot_water_shared_annual: text(settlement.hot_water_shared_annual),
    cold_water_price_per_m3: text(settlement.cold_water_price_per_m3),
    cold_water_shared_annual: text(settlement.cold_water_shared_annual),
    heat_water_price_per_m3: text(settlement.heat_water_price_per_m3),
    heat_water_base_annual: text(settlement.heat_water_base_annual),
    heating_annual: text(settlement.heating_annual),
    use_coefficients: !!settlement.heating_coefficients,
    heating_coefficients: (
      settlement.heating_coefficients ?? Array(12).fill(0)
    ).map(String),
    electricity_annual_consumption_cost: text(
      settlement.electricity_annual_consumption_cost
    ),
    electricity_annual_fixed_cost: text(
      settlement.electricity_annual_fixed_cost
    ),
    electricity_annual_kwh: text(settlement.electricity_annual_kwh),
    electricity_bill_period_start: text(
      settlement.electricity_bill_period_start
    ),
    electricity_bill_period_end: text(settlement.electricity_bill_period_end),
    tenant_note: text(settlement.tenant_note),
    internal_note: text(settlement.internal_note)
  }
}

function toPayload(values: SettlementFormValues): SettlementUpdate {
  return {
    title: values.title,
    period_start: values.period_start,
    period_end: values.period_end,
    costs_period_start: values.costs_period_start,
    costs_period_end: values.costs_period_end,
    proration_base: values.proration_base,
    period_months_override: orNull(values.period_months_override),
    hot_water_price_per_m3: orNull(values.hot_water_price_per_m3),
    hot_water_shared_annual: values.hot_water_shared_annual || '0',
    cold_water_price_per_m3: orNull(values.cold_water_price_per_m3),
    cold_water_shared_annual: values.cold_water_shared_annual || '0',
    heat_water_price_per_m3: orNull(values.heat_water_price_per_m3),
    heat_water_base_annual: values.heat_water_base_annual || '0',
    heating_annual: values.heating_annual || '0',
    heating_coefficients: values.use_coefficients
      ? values.heating_coefficients.map((value) => typedNumber(value) || 0)
      : null,
    electricity_annual_consumption_cost: orNull(
      values.electricity_annual_consumption_cost
    ),
    electricity_annual_fixed_cost: orNull(values.electricity_annual_fixed_cost),
    electricity_annual_kwh: orNull(values.electricity_annual_kwh),
    electricity_bill_period_start:
      values.electricity_bill_period_start || undefined,
    electricity_bill_period_end:
      values.electricity_bill_period_end || undefined,
    tenant_note: values.tenant_note,
    internal_note: values.internal_note
  }
}

/**
 * One form for every numeric input of the settlement. The wizard steps each
 * render a slice of it, but there is a single payload and a single save, so
 * moving between steps can never lose a half-typed value.
 */
export function useSettlementInputsForm(settlement: SettlementDetail) {
  const update = useUpdateSettlement()
  const form = useForm<SettlementFormValues>({
    mode: 'onTouched',
    defaultValues: toFormValues(settlement)
  })

  useEffect(() => {
    form.reset(toFormValues(settlement))
    // Resetting on every render of a new server object; `form` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlement])

  const save = () =>
    new Promise<void>((resolve) => {
      if (settlement.status !== 'DRAFT') return resolve()
      if (!form.formState.isDirty) return resolve()

      update.mutate(
        { id: settlement.id, body: toPayload(form.getValues()) },
        { onSettled: () => resolve() }
      )
    })

  return { form, save, isSaving: update.isPending }
}
